import React from 'react';
import type { ISettingsColor, ISettingsFont, IStyleParam } from '@wix/tpa-settings';
import { useStyles } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { ImageResizeOptions } from 'wix-ui-tpa/cssVars';
import { Add } from '@wix/wix-ui-icons-common';
import { Cart, EcomCart, ShoppingBag, Stores } from '@wix/wix-ui-icons-common/on-stage';
import type { StyleProperties } from 'root/contexts/ItemsContext';
import { Alignment } from 'root/consts';
import type { FeaturedBadgePlacement } from 'root/consts';
import { getAlignmentText } from 'root/utils/alignment';
import { stylesParams } from 'root/components/MenuAddOn/stylesParams';
import {
  ADD_TO_CART_BUTTON_RADIUS_MAX,
  AddToCartButtonContent,
  AddToCartButtonIconType,
  AddToCartButtonWidth,
} from 'root/components/MenuAddOn/Settings/Tabs/DesignTab/AddToCartButtonDesign/consts';
import {
  DEFAULT_BACKGROUND_HORIZONTAL_PADDING,
  DEFAULT_BACKGROUND_VERTICAL_PADDING,
  DEFAULT_FEATURED_BADGE_PLACEMENT,
  DEFAULT_ITEM_IMAGE_RATIO,
  DEFAULT_ITEM_IMAGE_RESIZE_MODE,
  DEFAULT_ITEM_IMAGE_SIDE_PADDING,
  DEFAULT_ITEM_IMAGE_VERTICAL_PADDING,
  DEFAULT_ITEM_VERTICAL_PADDING,
  DEFAULT_ITEMS_COLUMN_GAP,
  DEFAULT_ITEMS_PER_LOAD,
  DEFAULT_ITEMS_ROW_GAP,
  DEFAULT_ITEM_INFO_SIDE_PADDING,
  DEFAULT_MAX_ITEMS_PER_ROW,
  DEFAULT_SLIDER_ARROW_DISTANCE,
  ITEM_IMAGE_PADDING_MAX,
  ITEM_INFO_SIDE_PADDING_MAX,
  ITEMS_PER_LOAD_MAX,
  ITEMS_PER_LOAD_MIN,
  MENU_ADD_ON_SPACING_MAX,
  MENU_ADD_ON_SPACING_MIN,
  MAX_ITEMS_PER_ROW_MAX,
  MAX_ITEMS_PER_ROW_MIN,
  MenuAddOnImageResizeMode,
  MenuAddOnItemsDisplayMode,
  MenuAddOnSliderArrowPosition,
  SLIDER_ARROW_DISTANCE_MAX,
  SLIDER_ARROW_DISTANCE_MIN,
  clampStyleNumber,
} from 'root/components/MenuAddOn/Settings/Tabs/LayoutTab/consts';

const ADD_TO_CART_ICONS = [Cart, EcomCart, Stores, ShoppingBag, Add] as const;

type WidgetRootStyle = React.CSSProperties & {
  '--menuAddOnBackgroundHorizontalPadding': string;
  '--menuAddOnBackgroundVerticalPadding': string;
  '--menuAddOnOrderingBannerIconColor': string;
  '--menuAddOnOrderingBannerIconDisplay': string;
  '--menuAddOnOrderingBannerIconSize': string;
  '--menuAddOnOrderingBannerTextColor': string;
  '--menuAddOnOrderingBannerTextColor-rgb': string;
  '--menuAddOnOrderingBannerTextColor-opacity': number;
  '--menuAddOnOrderingBannerTextTheme-family': string;
  '--menuAddOnOrderingBannerTextTheme-size': string;
  '--menuAddOnOrderingBannerTextTheme-style': string;
  '--menuAddOnOrderingBannerTextTheme-weight': string;
  '--menuAddOnOrderingBannerTextTheme-line-height': string;
  '--menuAddOnOrderingBannerTextTheme-text-decoration': string;
  '--menuAddOnOrderingBannerTextFontFamily': string;
  '--menuAddOnOrderingBannerTextFontSize': string;
  '--menuAddOnOrderingBannerTextFontStyle': string;
  '--menuAddOnOrderingBannerTextFontWeight': string;
  '--menuAddOnOrderingBannerTextLineHeight': string;
  '--menuAddOnOrderingBannerTextDecoration': string;
  '--menuAddOnOrderingBannerChangeButtonBackgroundColor': string;
  '--menuAddOnOrderingBannerChangeButtonBackgroundColor-rgb': string;
  '--menuAddOnOrderingBannerChangeButtonBackgroundColor-opacity': number;
  '--menuAddOnOrderingBannerChangeButtonBorderColor': string;
  '--menuAddOnOrderingBannerChangeButtonBorderColor-rgb': string;
  '--menuAddOnOrderingBannerChangeButtonBorderColor-opacity': number;
  '--menuAddOnOrderingBannerChangeButtonBorderRadius': string;
  '--menuAddOnOrderingBannerChangeButtonBorderWidth': number;
  '--menuAddOnOrderingBannerChangeButtonTextColor': string;
  '--menuAddOnOrderingBannerChangeButtonTextColor-rgb': string;
  '--menuAddOnOrderingBannerChangeButtonTextColor-opacity': number;
  '--menuAddOnOrderingBannerChangeButtonTextTheme-family': string;
  '--menuAddOnOrderingBannerChangeButtonTextTheme-size': string;
  '--menuAddOnOrderingBannerChangeButtonTextTheme-style': string;
  '--menuAddOnOrderingBannerChangeButtonTextTheme-weight': string;
  '--menuAddOnOrderingBannerChangeButtonTextTheme-line-height': string;
  '--menuAddOnOrderingBannerChangeButtonTextTheme-text-decoration': string;
  '--menuAddOnOrderingBannerChangeButtonTextFontFamily': string;
  '--menuAddOnOrderingBannerChangeButtonTextFontSize': string;
  '--menuAddOnOrderingBannerChangeButtonTextFontStyle': string;
  '--menuAddOnOrderingBannerChangeButtonTextFontWeight': string;
  '--menuAddOnOrderingBannerChangeButtonTextLineHeight': string;
  '--menuAddOnOrderingBannerChangeButtonTextDecoration': string;
  '--menuAddOnOrderingBannerViewCartButtonBackgroundColor': string;
  '--menuAddOnOrderingBannerViewCartButtonBackgroundColor-rgb': string;
  '--menuAddOnOrderingBannerViewCartButtonBackgroundColor-opacity': number;
  '--menuAddOnOrderingBannerViewCartButtonBorderColor': string;
  '--menuAddOnOrderingBannerViewCartButtonBorderColor-rgb': string;
  '--menuAddOnOrderingBannerViewCartButtonBorderColor-opacity': number;
  '--menuAddOnOrderingBannerViewCartButtonBorderRadius': string;
  '--menuAddOnOrderingBannerViewCartButtonBorderWidth': number;
  '--menuAddOnOrderingBannerViewCartButtonTextColor': string;
  '--menuAddOnOrderingBannerViewCartButtonTextColor-rgb': string;
  '--menuAddOnOrderingBannerViewCartButtonTextColor-opacity': number;
  '--menuAddOnOrderingBannerViewCartButtonTextTheme-family': string;
  '--menuAddOnOrderingBannerViewCartButtonTextTheme-size': string;
  '--menuAddOnOrderingBannerViewCartButtonTextTheme-style': string;
  '--menuAddOnOrderingBannerViewCartButtonTextTheme-weight': string;
  '--menuAddOnOrderingBannerViewCartButtonTextTheme-line-height': string;
  '--menuAddOnOrderingBannerViewCartButtonTextTheme-text-decoration': string;
  '--menuAddOnOrderingBannerViewCartButtonTextFontFamily': string;
  '--menuAddOnOrderingBannerViewCartButtonTextFontSize': string;
  '--menuAddOnOrderingBannerViewCartButtonTextFontStyle': string;
  '--menuAddOnOrderingBannerViewCartButtonTextFontWeight': string;
  '--menuAddOnOrderingBannerViewCartButtonTextLineHeight': string;
  '--menuAddOnOrderingBannerViewCartButtonTextDecoration': string;
  '--menuAddOnOrderingBannerBackgroundColor': string;
  '--menuAddOnOrderingBannerBackgroundColor-rgb': string;
  '--menuAddOnOrderingBannerBackgroundColor-opacity': number;
  '--menuAddOnOrderingBannerBorderColor': string;
  '--menuAddOnOrderingBannerBorderColor-rgb': string;
  '--menuAddOnOrderingBannerBorderColor-opacity': number;
  '--menuAddOnOrderingBannerBorderRadius': string;
  '--menuAddOnOrderingBannerBorderWidth': number;
};

type ItemsLayoutStyle = React.CSSProperties & {
  '--firstItemDividerMarginBottom': string;
  '--itemRootMargin': string;
  '--itemsColumnGap': string;
  '--itemsDisplay': string;
  '--itemsRowGap': string;
  '--itemImageAspectRatio': number;
  '--itemTextAlignment': string;
  '--menuAddOnItemImageObjectFit': string;
  '--menuAddOnItemImageOpacity': number;
  '--menuAddOnItemImageSidePadding': string;
  '--menuAddOnItemImageVerticalPadding': string;
  '--menuAddOnItemInfoSidePadding': string;
  '--menuAddOnItemVerticalPadding': string;
  '--menuAddOnCardsJustifyContent': string;
  '--menuAddOnGridItemWidth': string;
  '--menuAddOnSliderArrowDistance': string;
  '--menuAddOnSliderArrowSize': string;
  '--menuAddOnSliderArrowColorValue': string;
  '--menuAddOnSliderArrowContainerColorValue': string;
  '--menuAddOnSliderArrowContainerBorderRadius': string;
  '--menuAddOnSliderArrowContainerBorderWidth': string;
  '--menuAddOnSliderArrowContainerBorderColorValue': string;
  '--menuAddOnSliderItemWidth': string;
  '--menuAddOnAddToCartButtonJustifyContent': string;
  '--nameAndPriceJustifyContent': string;
  '--showItemDividerContainer': string;
  '--showItemDividers': string;
};

export const getJustifyContent = (alignment: Alignment) => {
  switch (alignment) {
    case Alignment.Center:
      return 'center';
    case Alignment.End:
      return 'flex-end';
    case Alignment.Start:
    default:
      return 'flex-start';
  }
};

const getImageResizeOption = (resizeMode: MenuAddOnImageResizeMode) =>
  resizeMode === MenuAddOnImageResizeMode.Fit ? ImageResizeOptions.contain : ImageResizeOptions.cover;

const getImageObjectFit = (resizeMode: MenuAddOnImageResizeMode) =>
  resizeMode === MenuAddOnImageResizeMode.Fit ? 'contain' : 'cover';

const hexToRgb = (hex: string) => {
  const normalizedHex = hex.replace('#', '').trim();
  const fullHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split('')
          .map((part) => `${part}${part}`)
          .join('')
      : normalizedHex;

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
    return null;
  }

  return `${parseInt(fullHex.slice(0, 2), 16)}, ${parseInt(fullHex.slice(2, 4), 16)}, ${parseInt(
    fullHex.slice(4, 6),
    16
  )}`;
};

const getCssColorValue = (color: ISettingsColor | undefined, fallback: string) => {
  if (!color?.value) {
    return fallback;
  }

  const opacity = color.opacity ?? 1;
  const rgbMatch = color.value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
  }

  const hexRgb = hexToRgb(color.value);
  if (hexRgb) {
    return `rgba(${hexRgb}, ${opacity})`;
  }

  return color.value;
};

const getCssColorVariable = (color: ISettingsColor | undefined, fallbackRgb: string, fallbackOpacity = 1) => {
  const rgbMatch = color?.value?.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
  const rgb = rgbMatch ? `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}` : color?.value ? hexToRgb(color.value) : null;
  const opacity = color?.opacity ?? (rgbMatch?.[4] ? Number(rgbMatch[4]) : fallbackOpacity);

  return {
    value: `${rgb ?? fallbackRgb}, ${opacity}`,
    rgb: rgb ?? fallbackRgb,
    opacity,
  };
};

const getCssFontVars = (font: ISettingsFont | undefined) => {
  const fontStyle = font?.style;
  const isUnderline = Boolean(fontStyle?.underline || (font as ISettingsFont & { underline?: boolean })?.underline);

  return {
    family: font?.cssFontFamily || font?.family || 'inherit',
    size: font?.size ? `${font.size}px` : 'inherit',
    style: fontStyle?.italic ? 'italic' : 'normal',
    weight: fontStyle?.bold ? '700' : 'inherit',
    lineHeight: font?.lineHeight ? `${font.lineHeight}px` : 'normal',
    textDecoration: isUnderline ? 'underline' : 'none',
  };
};

export const useMenuAddOnWidgetStyles = ({ isViewFullMenuButtonHovered }: { isViewFullMenuButtonHovered: boolean }) => {
  const styles = useStyles();
  const { isMobile } = useEnvironment();
  const getStyleBool = (key: string): boolean => {
    const param = (stylesParams as unknown as Record<string, IStyleParam>)[key];
    return param ? (styles.get(param) as boolean) : true;
  };

  const showTitle = getStyleBool('showMenuName');
  const showSubtitle = getStyleBool('showMenuDescription');
  const showItemImage = getStyleBool('showItemImage');
  const headerAlignment = styles.get(stylesParams.menuAddOnHeaderAlignment) as Alignment;
  const maxItemsPerRow = clampStyleNumber(
    styles.get(stylesParams.menuAddOnMaxItemsPerRow),
    DEFAULT_MAX_ITEMS_PER_ROW,
    MAX_ITEMS_PER_ROW_MIN,
    MAX_ITEMS_PER_ROW_MAX
  );
  const itemsDisplayMode = styles.get(stylesParams.menuAddOnItemsDisplayMode) as MenuAddOnItemsDisplayMode;
  const itemsPerLoad = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemsPerLoad),
    DEFAULT_ITEMS_PER_LOAD,
    ITEMS_PER_LOAD_MIN,
    ITEMS_PER_LOAD_MAX
  );
  const itemsColumnGap = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemsColumnGap),
    DEFAULT_ITEMS_COLUMN_GAP,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const itemsRowGap = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemsRowGap),
    DEFAULT_ITEMS_ROW_GAP,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const cardAlignment = styles.get(stylesParams.menuAddOnCardAlignment) as Alignment;
  const itemInfoAlignment = styles.get(stylesParams.menuAddOnItemInfoAlignment) as Alignment;
  const itemInfoSidePadding = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemInfoSidePadding),
    DEFAULT_ITEM_INFO_SIDE_PADDING,
    MENU_ADD_ON_SPACING_MIN,
    ITEM_INFO_SIDE_PADDING_MAX
  );
  const itemVerticalPadding = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemVerticalPadding),
    DEFAULT_ITEM_VERTICAL_PADDING,
    MENU_ADD_ON_SPACING_MIN,
    ITEM_INFO_SIDE_PADDING_MAX
  );
  const itemImageRatio = (styles.get(stylesParams.menuAddOnItemImageRatio) as number) || DEFAULT_ITEM_IMAGE_RATIO;
  const itemImageResizeMode =
    (styles.get(stylesParams.menuAddOnItemImageResizeMode) as MenuAddOnImageResizeMode) ??
    DEFAULT_ITEM_IMAGE_RESIZE_MODE;
  const itemImageVerticalPadding = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemImageVerticalPadding),
    DEFAULT_ITEM_IMAGE_VERTICAL_PADDING,
    MENU_ADD_ON_SPACING_MIN,
    ITEM_IMAGE_PADDING_MAX
  );
  const itemImageSidePadding = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemImageSidePadding),
    DEFAULT_ITEM_IMAGE_SIDE_PADDING,
    MENU_ADD_ON_SPACING_MIN,
    ITEM_IMAGE_PADDING_MAX
  );
  const itemImageOpacity = clampStyleNumber(styles.get(stylesParams.menuAddOnItemImageOpacity), 100, 0, 100) / 100;
  const featuredBadgePlacement =
    (styles.get(stylesParams.menuAddOnFeaturedBadgePlacement) as FeaturedBadgePlacement) ??
    DEFAULT_FEATURED_BADGE_PLACEMENT;
  const backgroundHorizontalPadding = clampStyleNumber(
    styles.get(stylesParams.menuAddOnBackgroundHorizontalPadding),
    DEFAULT_BACKGROUND_HORIZONTAL_PADDING,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const backgroundVerticalPadding = clampStyleNumber(
    styles.get(stylesParams.menuAddOnBackgroundVerticalPadding),
    DEFAULT_BACKGROUND_VERTICAL_PADDING,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const sliderArrowDistance = clampStyleNumber(
    styles.get(stylesParams.menuAddOnSliderArrowDistance),
    DEFAULT_SLIDER_ARROW_DISTANCE,
    SLIDER_ARROW_DISTANCE_MIN,
    SLIDER_ARROW_DISTANCE_MAX
  );
  const sliderArrowSize = clampStyleNumber(styles.get(stylesParams.menuAddOnSliderArrowSize), 24, 18, 100);
  const sliderArrowContainerBorderRadius = clampStyleNumber(
    styles.get(stylesParams.menuAddOnSliderArrowContainerBorderRadius),
    0,
    0,
    50
  );
  const sliderArrowContainerBorderWidth = clampStyleNumber(
    styles.get(stylesParams.menuAddOnSliderArrowContainerBorderWidth),
    0,
    0,
    20
  );
  const sliderArrowColor = getCssColorValue(
    styles.get(stylesParams.menuAddOnSliderArrowColor) as ISettingsColor,
    'currentColor'
  );
  const sliderArrowContainerColor = getCssColorValue(
    styles.get(stylesParams.menuAddOnSliderArrowContainerColor) as ISettingsColor,
    'transparent'
  );
  const sliderArrowContainerBorderColor = getCssColorValue(
    styles.get(stylesParams.menuAddOnSliderArrowContainerBorderColor) as ISettingsColor,
    'transparent'
  );
  const viewFullMenuButtonBorderRadius = clampStyleNumber(
    styles.get(stylesParams.menuAddOnViewFullMenuButtonBorderRadius),
    0,
    0,
    50
  );
  const viewFullMenuButtonBorderWidth = clampStyleNumber(
    styles.get(stylesParams.menuAddOnViewFullMenuButtonBorderWidth),
    1,
    0,
    10
  );
  const viewFullMenuButtonTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnViewFullMenuButtonTextTheme) as ISettingsFont
  );
  const viewFullMenuButtonHoverTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnViewFullMenuButtonHoverTextTheme) as ISettingsFont
  );
  const loadMoreButtonFont = styles.get(stylesParams.menuAddOnLoadMoreButtonTextTheme) as ISettingsFont;
  const loadMoreButtonTextFont = getCssFontVars(loadMoreButtonFont);
  const addToCartButtonDefaultTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnAddToCartButtonDefaultTextTheme) as ISettingsFont
  );
  const addToCartButtonHoverTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnAddToCartButtonHoverTextTheme) as ISettingsFont
  );
  const addToCartButtonBorderWidth = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonDefaultBorderWidth),
    0,
    0,
    20
  );
  const addToCartButtonRadiusStartStart = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonBorderRadiusStartStart),
    0,
    0,
    ADD_TO_CART_BUTTON_RADIUS_MAX
  );
  const addToCartButtonRadiusStartEnd = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonBorderRadiusStartEnd),
    0,
    0,
    ADD_TO_CART_BUTTON_RADIUS_MAX
  );
  const addToCartButtonRadiusEndStart = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonBorderRadiusEndStart),
    0,
    0,
    ADD_TO_CART_BUTTON_RADIUS_MAX
  );
  const addToCartButtonRadiusEndEnd = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonBorderRadiusEndEnd),
    0,
    0,
    ADD_TO_CART_BUTTON_RADIUS_MAX
  );
  const addToCartButtonContent = styles.get(stylesParams.menuAddOnAddToCartButtonContent) as AddToCartButtonContent;
  const addToCartButtonIconType = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonIconType),
    AddToCartButtonIconType.Add,
    AddToCartButtonIconType.Cart,
    AddToCartButtonIconType.Add
  );
  const addToCartButtonFullWidth =
    (styles.get(stylesParams.menuAddOnAddToCartButtonWidth) as AddToCartButtonWidth) !== AddToCartButtonWidth.FitToText;
  const orderingBannerTextColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerTextColor) as ISettingsColor,
    '29, 29, 31'
  );
  const orderingBannerTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnOrderingBannerTextTheme) as ISettingsFont
  );
  const orderingBannerIconSize = clampStyleNumber(styles.get(stylesParams.menuAddOnOrderingBannerIconSize), 24, 8, 48);
  const showOrderingBannerIcon = styles.get(stylesParams.menuAddOnOrderingBannerShowIcon) as boolean;
  const orderingBannerChangeButtonTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnOrderingBannerChangeButtonTextTheme) as ISettingsFont
  );
  const orderingBannerChangeButtonBorderWidth = clampStyleNumber(
    styles.get(stylesParams.menuAddOnOrderingBannerChangeButtonBorderWidth),
    1,
    0,
    20
  );
  const orderingBannerChangeButtonBorderRadius = clampStyleNumber(
    styles.get(stylesParams.menuAddOnOrderingBannerChangeButtonBorderRadius),
    0,
    0,
    25
  );
  const orderingBannerViewCartButtonTextFont = getCssFontVars(
    styles.get(stylesParams.menuAddOnOrderingBannerViewCartButtonTextTheme) as ISettingsFont
  );
  const orderingBannerViewCartButtonBorderWidth = clampStyleNumber(
    styles.get(stylesParams.menuAddOnOrderingBannerViewCartButtonBorderWidth),
    0,
    0,
    20
  );
  const orderingBannerViewCartButtonBorderRadius = clampStyleNumber(
    styles.get(stylesParams.menuAddOnOrderingBannerViewCartButtonBorderRadius),
    0,
    0,
    25
  );
  const orderingBannerBorderWidth = clampStyleNumber(
    styles.get(stylesParams.menuAddOnOrderingBannerBorderWidth),
    1,
    0,
    20
  );
  const orderingBannerBorderRadius = clampStyleNumber(
    styles.get(stylesParams.menuAddOnOrderingBannerBorderRadius),
    0,
    0,
    25
  );
  const sliderArrowPosition = styles.get(stylesParams.menuAddOnSliderArrowPosition) as MenuAddOnSliderArrowPosition;
  const viewFullMenuButtonAlignment = styles.get(stylesParams.menuAddOnViewFullMenuButtonAlignment) as Alignment;
  const addToCartButtonAlignment = styles.get(stylesParams.menuAddOnAddToCartButtonAlignment) as Alignment;
  const loadMoreButtonAlignment = styles.get(stylesParams.menuAddOnLoadMoreButtonAlignment) as Alignment;
  const isSliderItemsLayout = itemsDisplayMode === MenuAddOnItemsDisplayMode.Slider;
  const areSliderArrowsOutside = sliderArrowPosition === MenuAddOnSliderArrowPosition.Outside;
  const displayedMaxItemsPerRow = isMobile ? (maxItemsPerRow > 2 ? 2 : maxItemsPerRow) : maxItemsPerRow;
  const itemWidth = `calc((100% - ${itemsColumnGap * (displayedMaxItemsPerRow - 1)}px) / ${displayedMaxItemsPerRow})`;

  const itemsLayoutStyle: ItemsLayoutStyle = {
    '--firstItemDividerMarginBottom': '0',
    '--itemRootMargin': '0',
    '--itemsColumnGap': `${itemsColumnGap}px`,
    '--itemsDisplay': 'flex',
    '--itemsRowGap': `${itemsRowGap}px`,
    '--itemImageAspectRatio': itemImageRatio,
    '--itemTextAlignment': getAlignmentText(itemInfoAlignment),
    '--menuAddOnItemImageObjectFit': getImageObjectFit(itemImageResizeMode),
    '--menuAddOnItemImageOpacity': itemImageOpacity,
    '--menuAddOnItemImageSidePadding': `${itemImageSidePadding}px`,
    '--menuAddOnItemImageVerticalPadding': `${itemImageVerticalPadding}px`,
    '--menuAddOnItemInfoSidePadding': `${itemInfoSidePadding}px`,
    '--menuAddOnItemVerticalPadding': `${itemVerticalPadding}px`,
    '--menuAddOnCardsJustifyContent': getJustifyContent(cardAlignment),
    '--menuAddOnGridItemWidth': itemWidth,
    '--menuAddOnSliderArrowDistance': `${sliderArrowDistance}px`,
    '--menuAddOnSliderArrowSize': `${sliderArrowSize}px`,
    '--menuAddOnSliderArrowColorValue': sliderArrowColor,
    '--menuAddOnSliderArrowContainerColorValue': sliderArrowContainerColor,
    '--menuAddOnSliderArrowContainerBorderRadius': `${sliderArrowContainerBorderRadius}px`,
    '--menuAddOnSliderArrowContainerBorderWidth': `${sliderArrowContainerBorderWidth}px`,
    '--menuAddOnSliderArrowContainerBorderColorValue': sliderArrowContainerBorderColor,
    '--menuAddOnSliderItemWidth': itemWidth,
    '--menuAddOnAddToCartButtonJustifyContent': getJustifyContent(addToCartButtonAlignment),
    '--nameAndPriceJustifyContent': getJustifyContent(itemInfoAlignment),
    '--showItemDividerContainer': 'none',
    '--showItemDividers': 'none',
  };
  const viewFullMenuButtonDefaultStyle: React.CSSProperties = {
    background: getCssColorValue(
      styles.get(stylesParams.menuAddOnViewFullMenuButtonBackgroundColor) as ISettingsColor,
      'transparent'
    ),
    borderColor: getCssColorValue(
      styles.get(stylesParams.menuAddOnViewFullMenuButtonBorderColor) as ISettingsColor,
      'currentColor'
    ),
    borderRadius: `${viewFullMenuButtonBorderRadius}px`,
    borderStyle: 'solid',
    borderWidth: `${viewFullMenuButtonBorderWidth}px`,
    color: getCssColorValue(
      styles.get(stylesParams.menuAddOnViewFullMenuButtonTextColor) as ISettingsColor,
      'currentColor'
    ),
    fontFamily: viewFullMenuButtonTextFont.family,
    fontSize: viewFullMenuButtonTextFont.size,
    fontStyle: viewFullMenuButtonTextFont.style,
    fontWeight: viewFullMenuButtonTextFont.weight,
    lineHeight: viewFullMenuButtonTextFont.lineHeight,
    textDecoration: viewFullMenuButtonTextFont.textDecoration,
  };
  const viewFullMenuButtonHoverStyle: React.CSSProperties = {
    background: getCssColorValue(
      styles.get(stylesParams.menuAddOnViewFullMenuButtonHoverBackgroundColor) as ISettingsColor,
      'transparent'
    ),
    borderColor: getCssColorValue(
      styles.get(stylesParams.menuAddOnViewFullMenuButtonHoverBorderColor) as ISettingsColor,
      'currentColor'
    ),
    color: getCssColorValue(
      styles.get(stylesParams.menuAddOnViewFullMenuButtonHoverTextColor) as ISettingsColor,
      'currentColor'
    ),
    fontFamily: viewFullMenuButtonHoverTextFont.family,
    fontSize: viewFullMenuButtonHoverTextFont.size,
    fontStyle: viewFullMenuButtonHoverTextFont.style,
    fontWeight: viewFullMenuButtonHoverTextFont.weight,
    lineHeight: viewFullMenuButtonHoverTextFont.lineHeight,
    textDecoration: viewFullMenuButtonHoverTextFont.textDecoration,
  };
  const viewFullMenuButtonStyle = isViewFullMenuButtonHovered
    ? { ...viewFullMenuButtonDefaultStyle, ...viewFullMenuButtonHoverStyle }
    : viewFullMenuButtonDefaultStyle;
  const loadMoreButtonStyle: React.CSSProperties = {
    color: getCssColorValue(styles.get(stylesParams.menuAddOnLoadMoreButtonTextColor) as ISettingsColor, 'inherit'),
    fontFamily: loadMoreButtonTextFont.family,
    fontSize: loadMoreButtonTextFont.size,
    fontStyle: loadMoreButtonTextFont.style,
    fontWeight: loadMoreButtonTextFont.weight,
    lineHeight: loadMoreButtonTextFont.lineHeight,
    textDecoration: loadMoreButtonFont?.fontStyleParam ? loadMoreButtonTextFont.textDecoration : 'underline',
  };
  const addToCartButtonStyle = React.useMemo<StyleProperties>(() => {
    const defaultTextColor = getCssColorVariable(
      styles.get(stylesParams.menuAddOnAddToCartButtonDefaultTextColor) as ISettingsColor,
      '255, 255, 255'
    );
    const defaultBackgroundColor = getCssColorVariable(
      styles.get(stylesParams.menuAddOnAddToCartButtonDefaultBackgroundColor) as ISettingsColor,
      '77, 56, 40'
    );
    const defaultBorderColor = getCssColorVariable(
      styles.get(stylesParams.menuAddOnAddToCartButtonDefaultBorderColor) as ISettingsColor,
      '0, 0, 0',
      0.12
    );
    const hoverTextColor = getCssColorVariable(
      styles.get(stylesParams.menuAddOnAddToCartButtonHoverTextColor) as ISettingsColor,
      defaultTextColor.rgb,
      defaultTextColor.opacity
    );
    const hoverBackgroundColor = getCssColorVariable(
      styles.get(stylesParams.menuAddOnAddToCartButtonHoverBackgroundColor) as ISettingsColor,
      defaultBackgroundColor.rgb,
      defaultBackgroundColor.opacity
    );
    const hoverBorderColor = getCssColorVariable(
      styles.get(stylesParams.menuAddOnAddToCartButtonHoverBorderColor) as ISettingsColor,
      defaultBorderColor.rgb,
      defaultBorderColor.opacity
    );

    return {
      '--menuAddOnAddToCartButtonDefaultTextColor': defaultTextColor.value,
      '--menuAddOnAddToCartButtonDefaultTextColor-rgb': defaultTextColor.rgb,
      '--menuAddOnAddToCartButtonDefaultTextColor-opacity': defaultTextColor.opacity,
      '--menuAddOnAddToCartButtonDefaultTextTheme-family': addToCartButtonDefaultTextFont.family,
      '--menuAddOnAddToCartButtonDefaultTextTheme-size': addToCartButtonDefaultTextFont.size,
      '--menuAddOnAddToCartButtonDefaultTextTheme-style': addToCartButtonDefaultTextFont.style,
      '--menuAddOnAddToCartButtonDefaultTextTheme-weight': addToCartButtonDefaultTextFont.weight,
      '--menuAddOnAddToCartButtonDefaultTextTheme-line-height': addToCartButtonDefaultTextFont.lineHeight,
      '--menuAddOnAddToCartButtonDefaultTextTheme-text-decoration': addToCartButtonDefaultTextFont.textDecoration,
      '--menuAddOnAddToCartButtonDefaultBackgroundColor': defaultBackgroundColor.value,
      '--menuAddOnAddToCartButtonDefaultBackgroundColor-rgb': defaultBackgroundColor.rgb,
      '--menuAddOnAddToCartButtonDefaultBackgroundColor-opacity': defaultBackgroundColor.opacity,
      '--menuAddOnAddToCartButtonDefaultBorderColor': defaultBorderColor.value,
      '--menuAddOnAddToCartButtonDefaultBorderColor-rgb': defaultBorderColor.rgb,
      '--menuAddOnAddToCartButtonDefaultBorderColor-opacity': defaultBorderColor.opacity,
      '--menuAddOnAddToCartButtonHoverTextColor': hoverTextColor.value,
      '--menuAddOnAddToCartButtonHoverTextColor-rgb': hoverTextColor.rgb,
      '--menuAddOnAddToCartButtonHoverTextColor-opacity': hoverTextColor.opacity,
      '--menuAddOnAddToCartButtonHoverTextTheme-family': addToCartButtonHoverTextFont.family,
      '--menuAddOnAddToCartButtonHoverTextTheme-size': addToCartButtonHoverTextFont.size,
      '--menuAddOnAddToCartButtonHoverTextTheme-style': addToCartButtonHoverTextFont.style,
      '--menuAddOnAddToCartButtonHoverTextTheme-weight': addToCartButtonHoverTextFont.weight,
      '--menuAddOnAddToCartButtonHoverTextTheme-line-height': addToCartButtonHoverTextFont.lineHeight,
      '--menuAddOnAddToCartButtonHoverTextTheme-text-decoration': addToCartButtonHoverTextFont.textDecoration,
      '--menuAddOnAddToCartButtonHoverBackgroundColor': hoverBackgroundColor.value,
      '--menuAddOnAddToCartButtonHoverBackgroundColor-rgb': hoverBackgroundColor.rgb,
      '--menuAddOnAddToCartButtonHoverBackgroundColor-opacity': hoverBackgroundColor.opacity,
      '--menuAddOnAddToCartButtonHoverBorderColor': hoverBorderColor.value,
      '--menuAddOnAddToCartButtonHoverBorderColor-rgb': hoverBorderColor.rgb,
      '--menuAddOnAddToCartButtonHoverBorderColor-opacity': hoverBorderColor.opacity,
      '--menuAddOnAddToCartButtonBorderWidth': addToCartButtonBorderWidth,
      '--menuAddOnAddToCartButtonBorderRadiusStartStart': addToCartButtonRadiusStartStart,
      '--menuAddOnAddToCartButtonBorderRadiusStartEnd': addToCartButtonRadiusStartEnd,
      '--menuAddOnAddToCartButtonBorderRadiusEndStart': addToCartButtonRadiusEndStart,
      '--menuAddOnAddToCartButtonBorderRadiusEndEnd': addToCartButtonRadiusEndEnd,
    };
  }, [
    addToCartButtonBorderWidth,
    addToCartButtonDefaultTextFont.family,
    addToCartButtonDefaultTextFont.lineHeight,
    addToCartButtonDefaultTextFont.size,
    addToCartButtonDefaultTextFont.style,
    addToCartButtonDefaultTextFont.textDecoration,
    addToCartButtonDefaultTextFont.weight,
    addToCartButtonRadiusEndEnd,
    addToCartButtonRadiusEndStart,
    addToCartButtonRadiusStartEnd,
    addToCartButtonRadiusStartStart,
    addToCartButtonHoverTextFont.family,
    addToCartButtonHoverTextFont.lineHeight,
    addToCartButtonHoverTextFont.size,
    addToCartButtonHoverTextFont.style,
    addToCartButtonHoverTextFont.textDecoration,
    addToCartButtonHoverTextFont.weight,
    styles,
  ]);
  const AddToCartIcon = ADD_TO_CART_ICONS[addToCartButtonIconType] ?? Add;
  const shouldShowAddToCartIcon = addToCartButtonContent !== AddToCartButtonContent.TextOnly;
  const shouldShowAddToCartLabel = addToCartButtonContent !== AddToCartButtonContent.IconOnly;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const menuBackgroundColor = (styles.get((stylesParams as any).menuBackgroundColor) as { value?: string } | undefined)
    ?.value;
  const orderingBannerBackgroundColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerBackgroundColor) as ISettingsColor,
    '255, 255, 255'
  );
  const orderingBannerBorderColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerBorderColor) as ISettingsColor,
    '88, 66, 48',
    0.32
  );
  const orderingBannerChangeButtonTextColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerChangeButtonTextColor) as ISettingsColor,
    '77, 56, 40'
  );
  const orderingBannerChangeButtonBackgroundColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerChangeButtonBackgroundColor) as ISettingsColor,
    '255, 255, 255',
    0
  );
  const orderingBannerChangeButtonBorderColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerChangeButtonBorderColor) as ISettingsColor,
    '111, 82, 59'
  );
  const orderingBannerViewCartButtonTextColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerViewCartButtonTextColor) as ISettingsColor,
    '255, 255, 255'
  );
  const orderingBannerViewCartButtonBackgroundColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerViewCartButtonBackgroundColor) as ISettingsColor,
    '77, 56, 40'
  );
  const orderingBannerViewCartButtonBorderColor = getCssColorVariable(
    styles.get(stylesParams.menuAddOnOrderingBannerViewCartButtonBorderColor) as ISettingsColor,
    '0, 0, 0',
    0
  );
  const widgetRootStyle: WidgetRootStyle = {
    '--menuAddOnBackgroundHorizontalPadding': `${backgroundHorizontalPadding}px`,
    '--menuAddOnBackgroundVerticalPadding': `${backgroundVerticalPadding}px`,
    '--menuAddOnOrderingBannerIconColor': getCssColorValue(
      styles.get(stylesParams.menuAddOnOrderingBannerIconColor) as ISettingsColor,
      'currentColor'
    ),
    '--menuAddOnOrderingBannerIconDisplay': showOrderingBannerIcon ? 'inline-flex' : 'none',
    '--menuAddOnOrderingBannerIconSize': `${orderingBannerIconSize}px`,
    '--menuAddOnOrderingBannerTextColor': orderingBannerTextColor.value,
    '--menuAddOnOrderingBannerTextColor-rgb': orderingBannerTextColor.rgb,
    '--menuAddOnOrderingBannerTextColor-opacity': orderingBannerTextColor.opacity,
    '--menuAddOnOrderingBannerTextTheme-family': orderingBannerTextFont.family,
    '--menuAddOnOrderingBannerTextTheme-size': orderingBannerTextFont.size,
    '--menuAddOnOrderingBannerTextTheme-style': orderingBannerTextFont.style,
    '--menuAddOnOrderingBannerTextTheme-weight': orderingBannerTextFont.weight,
    '--menuAddOnOrderingBannerTextTheme-line-height': orderingBannerTextFont.lineHeight,
    '--menuAddOnOrderingBannerTextTheme-text-decoration': orderingBannerTextFont.textDecoration,
    '--menuAddOnOrderingBannerTextFontFamily': orderingBannerTextFont.family,
    '--menuAddOnOrderingBannerTextFontSize': orderingBannerTextFont.size,
    '--menuAddOnOrderingBannerTextFontStyle': orderingBannerTextFont.style,
    '--menuAddOnOrderingBannerTextFontWeight': orderingBannerTextFont.weight,
    '--menuAddOnOrderingBannerTextLineHeight': orderingBannerTextFont.lineHeight,
    '--menuAddOnOrderingBannerTextDecoration': orderingBannerTextFont.textDecoration,
    '--menuAddOnOrderingBannerChangeButtonBackgroundColor': orderingBannerChangeButtonBackgroundColor.value,
    '--menuAddOnOrderingBannerChangeButtonBackgroundColor-rgb': orderingBannerChangeButtonBackgroundColor.rgb,
    '--menuAddOnOrderingBannerChangeButtonBackgroundColor-opacity': orderingBannerChangeButtonBackgroundColor.opacity,
    '--menuAddOnOrderingBannerChangeButtonBorderColor': orderingBannerChangeButtonBorderColor.value,
    '--menuAddOnOrderingBannerChangeButtonBorderColor-rgb': orderingBannerChangeButtonBorderColor.rgb,
    '--menuAddOnOrderingBannerChangeButtonBorderColor-opacity': orderingBannerChangeButtonBorderColor.opacity,
    '--menuAddOnOrderingBannerChangeButtonBorderRadius': `${orderingBannerChangeButtonBorderRadius}px`,
    '--menuAddOnOrderingBannerChangeButtonBorderWidth': orderingBannerChangeButtonBorderWidth,
    '--menuAddOnOrderingBannerChangeButtonTextColor': orderingBannerChangeButtonTextColor.value,
    '--menuAddOnOrderingBannerChangeButtonTextColor-rgb': orderingBannerChangeButtonTextColor.rgb,
    '--menuAddOnOrderingBannerChangeButtonTextColor-opacity': orderingBannerChangeButtonTextColor.opacity,
    '--menuAddOnOrderingBannerChangeButtonTextTheme-family': orderingBannerChangeButtonTextFont.family,
    '--menuAddOnOrderingBannerChangeButtonTextTheme-size': orderingBannerChangeButtonTextFont.size,
    '--menuAddOnOrderingBannerChangeButtonTextTheme-style': orderingBannerChangeButtonTextFont.style,
    '--menuAddOnOrderingBannerChangeButtonTextTheme-weight': orderingBannerChangeButtonTextFont.weight,
    '--menuAddOnOrderingBannerChangeButtonTextTheme-line-height': orderingBannerChangeButtonTextFont.lineHeight,
    '--menuAddOnOrderingBannerChangeButtonTextTheme-text-decoration': orderingBannerChangeButtonTextFont.textDecoration,
    '--menuAddOnOrderingBannerChangeButtonTextFontFamily': orderingBannerChangeButtonTextFont.family,
    '--menuAddOnOrderingBannerChangeButtonTextFontSize': orderingBannerChangeButtonTextFont.size,
    '--menuAddOnOrderingBannerChangeButtonTextFontStyle': orderingBannerChangeButtonTextFont.style,
    '--menuAddOnOrderingBannerChangeButtonTextFontWeight': orderingBannerChangeButtonTextFont.weight,
    '--menuAddOnOrderingBannerChangeButtonTextLineHeight': orderingBannerChangeButtonTextFont.lineHeight,
    '--menuAddOnOrderingBannerChangeButtonTextDecoration': orderingBannerChangeButtonTextFont.textDecoration,
    '--menuAddOnOrderingBannerViewCartButtonBackgroundColor': orderingBannerViewCartButtonBackgroundColor.value,
    '--menuAddOnOrderingBannerViewCartButtonBackgroundColor-rgb': orderingBannerViewCartButtonBackgroundColor.rgb,
    '--menuAddOnOrderingBannerViewCartButtonBackgroundColor-opacity':
      orderingBannerViewCartButtonBackgroundColor.opacity,
    '--menuAddOnOrderingBannerViewCartButtonBorderColor': orderingBannerViewCartButtonBorderColor.value,
    '--menuAddOnOrderingBannerViewCartButtonBorderColor-rgb': orderingBannerViewCartButtonBorderColor.rgb,
    '--menuAddOnOrderingBannerViewCartButtonBorderColor-opacity': orderingBannerViewCartButtonBorderColor.opacity,
    '--menuAddOnOrderingBannerViewCartButtonBorderRadius': `${orderingBannerViewCartButtonBorderRadius}px`,
    '--menuAddOnOrderingBannerViewCartButtonBorderWidth': orderingBannerViewCartButtonBorderWidth,
    '--menuAddOnOrderingBannerViewCartButtonTextColor': orderingBannerViewCartButtonTextColor.value,
    '--menuAddOnOrderingBannerViewCartButtonTextColor-rgb': orderingBannerViewCartButtonTextColor.rgb,
    '--menuAddOnOrderingBannerViewCartButtonTextColor-opacity': orderingBannerViewCartButtonTextColor.opacity,
    '--menuAddOnOrderingBannerViewCartButtonTextTheme-family': orderingBannerViewCartButtonTextFont.family,
    '--menuAddOnOrderingBannerViewCartButtonTextTheme-size': orderingBannerViewCartButtonTextFont.size,
    '--menuAddOnOrderingBannerViewCartButtonTextTheme-style': orderingBannerViewCartButtonTextFont.style,
    '--menuAddOnOrderingBannerViewCartButtonTextTheme-weight': orderingBannerViewCartButtonTextFont.weight,
    '--menuAddOnOrderingBannerViewCartButtonTextTheme-line-height': orderingBannerViewCartButtonTextFont.lineHeight,
    '--menuAddOnOrderingBannerViewCartButtonTextTheme-text-decoration':
      orderingBannerViewCartButtonTextFont.textDecoration,
    '--menuAddOnOrderingBannerViewCartButtonTextFontFamily': orderingBannerViewCartButtonTextFont.family,
    '--menuAddOnOrderingBannerViewCartButtonTextFontSize': orderingBannerViewCartButtonTextFont.size,
    '--menuAddOnOrderingBannerViewCartButtonTextFontStyle': orderingBannerViewCartButtonTextFont.style,
    '--menuAddOnOrderingBannerViewCartButtonTextFontWeight': orderingBannerViewCartButtonTextFont.weight,
    '--menuAddOnOrderingBannerViewCartButtonTextLineHeight': orderingBannerViewCartButtonTextFont.lineHeight,
    '--menuAddOnOrderingBannerViewCartButtonTextDecoration': orderingBannerViewCartButtonTextFont.textDecoration,
    '--menuAddOnOrderingBannerBackgroundColor': orderingBannerBackgroundColor.value,
    '--menuAddOnOrderingBannerBackgroundColor-rgb': orderingBannerBackgroundColor.rgb,
    '--menuAddOnOrderingBannerBackgroundColor-opacity': orderingBannerBackgroundColor.opacity,
    '--menuAddOnOrderingBannerBorderColor': orderingBannerBorderColor.value,
    '--menuAddOnOrderingBannerBorderColor-rgb': orderingBannerBorderColor.rgb,
    '--menuAddOnOrderingBannerBorderColor-opacity': orderingBannerBorderColor.opacity,
    '--menuAddOnOrderingBannerBorderRadius': `${orderingBannerBorderRadius}px`,
    '--menuAddOnOrderingBannerBorderWidth': orderingBannerBorderWidth,
    background: menuBackgroundColor,
  };

  return {
    AddToCartIcon,
    addToCartButtonFullWidth,
    addToCartButtonStyle,
    areSliderArrowsOutside,
    featuredBadgePlacement,
    headerAlignment,
    isSliderItemsLayout,
    itemImageResize: getImageResizeOption(itemImageResizeMode),
    itemsColumnGap,
    itemsLayoutStyle,
    itemsPerLoad,
    loadMoreButtonAlignment,
    loadMoreButtonStyle,
    maxItemsPerRow,
    shouldShowAddToCartIcon,
    shouldShowAddToCartLabel,
    showItemImage,
    showSubtitle,
    showTitle,
    viewFullMenuButtonAlignment,
    viewFullMenuButtonStyle,
    widgetRootStyle,
  };
};
